import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AppLayout } from "@/components/layout/AppLayout";
import { usePlaybooks, getPlaybookSlug } from "@/hooks/usePlaybooks";
import { getIcon } from "@/lib/iconMap";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SharePlaybookDialog } from "@/components/SharePlaybookDialog";
import { 
  BookOpen, 
  Clock, 
  ArrowRight,
  Lock,
  Loader2,
  Share2
} from "lucide-react";
import analytics from "@/lib/segment";

const categoryColors: Record<string, string> = {
  survival: "bg-destructive/10 text-destructive",
  operations: "bg-info/10 text-info",
  scaling: "bg-success/10 text-success",
};

export default function Playbooks() {
  const navigate = useNavigate();
  const [userTier] = useState<"free" | "pro" | "scale">("free"); // TODO: Get from profile
  const [sharePlaybook, setSharePlaybook] = useState<{ title: string; slug: string } | null>(null);
  const { data: playbooks, isLoading } = usePlaybooks();

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth");
      }
    };
    checkAuth();
  }, [navigate]);

  const navigateToPlaybook = (slug: string) => {
    navigate(`/app/playbooks/${slug}`);
  };

  const canAccess = (playbookTier: string) => {
    if (userTier === "scale") return true;
    if (userTier === "pro") return playbookTier !== "scale";
    return playbookTier === "free";
  };


  return (
    <AppLayout>
      <div className="p-4 md:p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            <BookOpen className="h-8 w-8 text-primary" />
            Playbooks
          </h1>
          <p className="text-muted-foreground mt-2">
            Expert guides for every hosting situation. Follow step-by-step to handle anything.
          </p>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap gap-2 mb-8">
          <Badge variant="secondary" className="cursor-pointer hover:bg-secondary/80">
            All Playbooks
          </Badge>
          <Badge variant="outline" className="cursor-pointer hover:bg-muted">
            Survival Mode
          </Badge>
          <Badge variant="outline" className="cursor-pointer hover:bg-muted">
            Operations
          </Badge>
          <Badge variant="outline" className="cursor-pointer hover:bg-muted">
            Scaling
          </Badge>
        </div>

        {/* Playbook Grid */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : playbooks?.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            No playbooks available yet.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {playbooks?.map((playbook) => {
              const hasAccess = canAccess(playbook.tier);
              const Icon = getIcon(playbook.icon_name);
              return (
                <Card 
                  key={playbook.id} 
                  className={`relative transition-all hover:shadow-md ${!hasAccess ? "opacity-75" : ""}`}
                >
                  {!hasAccess && (
                    <div className="absolute top-4 right-4">
                      <Lock className="h-5 w-5 text-muted-foreground" />
                    </div>
                  )}
                  <CardHeader>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="h-6 w-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={categoryColors[playbook.category] || "bg-muted"} variant="secondary">
                            {playbook.category}
                          </Badge>
                          {playbook.tier !== "free" && (
                            <Badge variant="outline" className="text-xs">
                              {playbook.tier.toUpperCase()}
                            </Badge>
                          )}
                        </div>
                        <CardTitle className="text-lg text-foreground">{playbook.title}</CardTitle>
                      </div>
                    </div>
                    <CardDescription className="mt-3">{playbook.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {playbook.estimated_time}
                      </span>
                      <div className="flex items-center gap-2">
                        {hasAccess && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSharePlaybook({ title: playbook.title, slug: getPlaybookSlug(playbook.title) });
                            }}
                          >
                            <Share2 className="h-4 w-4" />
                          </Button>
                        )}
                        <Button 
                          variant={hasAccess ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            if (hasAccess) {
                              navigateToPlaybook(getPlaybookSlug(playbook.title));
                            } else {
                              analytics.track("Upgrade CTA Clicked", {
                                source: "playbooks_locked",
                                playbook_title: playbook.title,
                                playbook_tier: playbook.tier,
                                user_tier: userTier,
                              });
                              navigate("/app/upgrade");
                            }
                          }}
                        >
                          {hasAccess ? (
                            <>
                              Start <ArrowRight className="ml-1 h-4 w-4" />
                            </>
                          ) : (
                            "Upgrade to Access"
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <SharePlaybookDialog
          open={!!sharePlaybook}
          onOpenChange={(open) => !open && setSharePlaybook(null)}
          playbookTitle={sharePlaybook?.title || ""}
          playbookSlug={sharePlaybook?.slug || ""}
        />
      </div>
    </AppLayout>
  );
}
